import { useState } from "react";
import { Modal } from "react-bootstrap";
import type { PopulationSegment } from "@models/populationSegment.model";

export type LinkTargetKind = "campaign" | "spec";

type Props = {
  kind: LinkTargetKind;
  entityName: string;
  segments: readonly PopulationSegment[];
  isSaving: boolean;
  onClose: () => void;
  /** explicitNone is only ever true for campaigns: "no population" was chosen deliberately. */
  onSave: (segmentId: number | null, explicitNone: boolean) => void;
};

const EXPLICIT_NONE = "none";

/*
    Fixes one orphan from the population page: attach a campaign or data spec
    to a population. Campaigns also get the deliberate "no
    population" choice (lookalike / DB-fed), which resolves the gap without
    linking anything.
*/
export default function LinkToPopulationModal({
  kind,
  entityName,
  segments,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [value, setValue] = useState("");

  const label = { campaign: "campaign", spec: "data spec" }[kind];
  const canSave = value !== "" && !isSaving;

  const handleSave = () => {
    if (value === EXPLICIT_NONE) onSave(null, true);
    else onSave(Number(value), false);
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>Link {label} to a population</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          {{ campaign: "Campaign", spec: "Data spec" }[kind]}:{" "}
          <span className="fw-semibold">{entityName}</span>
        </div>
        <label htmlFor="linkPopulation" className="form-label small fw-semibold">
          Population
        </label>
        <select
          id="linkPopulation"
          className="form-select"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        >
          <option value="">Choose a population</option>
          {segments.map((segment) => (
            <option key={segment.id} value={segment.id}>
              {segment.name}
            </option>
          ))}
          {kind === "campaign" && (
            <option value={EXPLICIT_NONE}>No population — lookalike / DB-fed</option>
          )}
        </select>
        {/* Sharing a population is allowed but never silent — same rule as the
            campaign form. */}
        {kind === "campaign" &&
          (() => {
            const selected = segments.find((s) => String(s.id) === value);
            if (!selected || selected.launch_count === 0) return null;
            return (
              <div className="alert alert-warning d-flex align-items-start gap-2 mt-2 mb-0 py-2 small">
                <i className="bi bi-exclamation-triangle-fill" />
                <div>
                  <strong>{selected.name}</strong> is already in use by {selected.launch_count}{" "}
                  {selected.launch_count === 1 ? "campaign" : "campaigns"}. This campaign will
                  share the same roster of people.
                </div>
              </div>
            );
          })()}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm btn-primary" disabled={!canSave} onClick={handleSave}>
          Save
        </button>
      </Modal.Footer>
    </Modal>
  );
}
